import React, { useEffect, useState, useCallback } from "react";
import baseUrl from "../../../api/baseUrl";
import InputText from "../../../components/inputs/InputText";
import Select from "react-select";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import useSubmitting from "../../../hooks/useSubmitting";
import { addCustomItem } from "../../../store/pos/posSlice";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";

interface InstantItemProps {
  setopen: (value: boolean) => void;
  open: boolean;
  setTableData?: any;
}

interface FormData {
  branchId: string;
  martId: string;
  createdBy: string;
  updatedBy: string;
  itemId: string;
  companyId: string;
  price: string;
  description: string;
  status: string;
  quantity: string;
}

const initialFormState: FormData = {
  branchId: "",
  martId: "",
  createdBy: "",
  updatedBy: "",
  itemId: "",
  companyId: "",
  price: "",
  description: "",
  status: "added",
  quantity: "",
};

const btnGhost =
  "inline-flex h-10 items-center justify-center rounded-lg border border-erp-line bg-white px-4 text-[12px] font-bold text-erp-muted transition-colors hover:border-erp-muted hover:text-erp-ink disabled:cursor-not-allowed disabled:opacity-60";

const btnPrimary =
  "inline-flex h-10 items-center justify-center rounded-lg border border-erp-navy bg-erp-navy px-4 text-[12px] font-bold text-white shadow-[0_4px_12px_rgba(11,28,44,0.16)] transition-colors hover:border-erp-navy-mid hover:bg-erp-navy-mid disabled:cursor-not-allowed disabled:opacity-60";

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: state.isFocused ? "#1f6f8b" : "#d7e0ea",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(31,111,139,0.14)" : "none",
    "&:hover": { borderColor: state.isFocused ? "#1f6f8b" : "#8ea3b8" },
    fontSize: 13,
    fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid #d7e0ea",
    boxShadow: "0 12px 28px rgba(11,28,44,0.12)",
    zIndex: 20,
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 13,
    backgroundColor: state.isSelected
      ? "#0b1c2c"
      : state.isFocused
        ? "rgba(31,111,139,0.08)"
        : "#fff",
    color: state.isSelected ? "#fff" : "#0b1c2c",
    fontWeight: state.isSelected ? 700 : 500,
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#8ea3b8",
    fontWeight: 500,
  }),
};

const InstantItem: React.FC<InstantItemProps> = ({ setopen, open }) => {
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [allProduct, setProductList] = useState<any[]>([]);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const dispatch = useDispatch();
  const [submitting, wrap] = useSubmitting();

  const fetchProducts = useCallback(() => {
    baseUrl
      .get("/branch/item/get-zero-stock-items", {
        headers: {
          limit: 100000,
          page: 1,
        },
      })
      .then((res) => {
        const items = res.data.data.allItems || [];
        setProductList(items);
        setProductOptions(
          items.map((item: any) => ({
            value: item.id,
            label: item.name,
            id: item.id,
            name: item.name,
          })),
        );
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchProducts();
    setFormData(initialFormState);
    setIsAddingNew(false);
    setNewProductName("");
    setInputValue("");
  }, [fetchProducts, open]);

  const handleProductChange = (selectedOption: any) => {
    if (selectedOption) {
      if ((selectedOption as any).isNew) {
        setIsAddingNew(true);
        setNewProductName(inputValue);
        setFormData((prev) => ({
          ...prev,
          itemId: "",
          price: "",
          quantity: "",
          description: "",
        }));
      } else {
        setIsAddingNew(false);
        setNewProductName("");
        setFormData((prev) => ({
          ...prev,
          itemId: selectedOption.value,
          price: "",
          quantity: "",
          description: "",
        }));
      }
    } else {
      setIsAddingNew(false);
      setNewProductName("");
      setFormData((prev) => ({
        ...prev,
        itemId: "",
        price: "",
        quantity: "",
        description: "",
      }));
    }
  };

  const handleClose = () => {
    setFormData(initialFormState);
    setIsAddingNew(false);
    setNewProductName("");
    setInputValue("");
    setopen(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isAddingNew) {
      if (!newProductName || !formData.price || !formData.quantity) {
        toast.error("Please enter name, price and quantity");
        return;
      }
      try {
        const res = await baseUrl.post("/branch/item/create-instant-item", {
          name: newProductName,
          price: Number(formData.price),
          stock: Number(formData.quantity),
          status: "Active",
        });
        const newItem = res.data?.data;
        if (newItem) {
          toast.success("Product added successfully");
          setProductList((prev) => [...prev, newItem]);
          setProductOptions((prev) => [
            ...prev,
            {
              value: newItem.id,
              label: newItem.name,
              id: newItem.id,
              name: newItem.name,
            },
          ]);
          const payload = {
            ...newItem,
            ...formData,
            itemId: newItem.id,
            price: Number(formData.price),
            quantity: Number(formData.quantity),
            description: formData.description || "",
            status: "added",
          };
          dispatch(addCustomItem(payload));
          handleClose();
        }
      } catch {
        toast.error("Failed to add product");
      }
      return;
    }

    if (!formData.itemId) {
      toast.error("Please select a product");
      return;
    }

    const selectedProduct =
      allProduct.find(
        (item: any) => String(item.id) === String(formData.itemId),
      ) || {};

    const payload = {
      ...selectedProduct,
      ...formData,
      branchId: formData.branchId || "1",
      martId: formData.martId || "1",
      createdBy: formData.createdBy || "1",
      updatedBy: formData.updatedBy || "1",
      companyId: formData.companyId || "1",
      status: formData.status || "added",
      quantity: parseInt(formData.quantity, 10) || 1,
      price: parseInt(formData.price, 10) || selectedProduct.price || 0,
      description: formData.description || selectedProduct.description || "",
    };

    dispatch(addCustomItem(payload));
    handleClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-erp-navy/55 p-4 backdrop-blur-[3px]"
      onClick={handleOverlayClick}
    >
      <div className="flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-2xl border border-erp-line bg-white font-inter shadow-[0_24px_60px_rgba(11,28,44,0.28)]">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-erp-navy via-erp-navy-mid to-erp-navy-soft px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-erp-gold/40 bg-erp-gold/20 text-erp-gold">
              <InventoryIcon className="!text-[20px]" />
            </div>
            <div className="min-w-0">
              <h2 className="m-0 text-[16px] font-bold tracking-wide text-white">
                Add Instant Item
              </h2>
              <p className="m-0 mt-0.5 text-[11px] font-medium text-white/60">
                Select an existing product or create a new one
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex rounded-lg border border-white/15 bg-white/10 p-1.5 text-white/85 transition-colors hover:bg-white/20 hover:text-white"
          >
            <CloseIcon className="!text-[18px]" />
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={wrap(handleSubmit)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5 [scrollbar-width:thin]">
            <div>
              <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.05em] text-erp-muted">
                Product
              </label>
              <Select
                id="dropdown"
                options={
                  inputValue &&
                  !productOptions.some(
                    (opt) =>
                      opt.label.toLowerCase() === inputValue.toLowerCase(),
                  )
                    ? [
                        ...productOptions,
                        {
                          value: "__add_new__",
                          label: `Add new product: "${inputValue}"`,
                          isNew: true,
                        },
                      ]
                    : productOptions
                }
                onInputChange={(val) => setInputValue(val)}
                onChange={handleProductChange}
                classNamePrefix="react-select"
                name="itemId"
                styles={selectStyles}
                value={
                  isAddingNew
                    ? {
                        value: "__add_new__",
                        label: `Add new product: "${newProductName || inputValue}"`,
                        isNew: true,
                      }
                    : productOptions.find(
                        (opt) => opt.value === formData.itemId,
                      ) || null
                }
                placeholder="Select or search product..."
                isClearable
              />
              {isAddingNew ? (
                <p className="m-0 mt-1.5 text-[11px] font-medium text-erp-accent">
                  Creating a new product from your search
                </p>
              ) : null}
            </div>

            {isAddingNew ? (
              <InputText
                name="newProductName"
                label="Product Name"
                value={newProductName}
                onChange={(e) => setNewProductName(e.target.value)}
                required
              />
            ) : null}

            <InputText
              name="quantity"
              label="Quantity"
              type="number"
              required
              value={formData.quantity}
              onChange={handleChange}
            />

            {isAddingNew || formData.itemId ? (
              <InputText
                name="price"
                label="Unit Price"
                type="number"
                required={isAddingNew}
                value={formData.price}
                onChange={handleChange}
              />
            ) : null}

            <InputText
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <div className="flex shrink-0 justify-end gap-2.5 border-t border-erp-line bg-white px-5 py-4">
            <button
              type="button"
              className={btnGhost}
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={btnPrimary}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Add Item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstantItem;
